import { Box, IconButton, Input, InputGroup } from '@chakra-ui/react';
import { CloseIcon, SearchIcon } from '@src/components/sco/icons';
import { useDebounce } from '@uidotdev/usehooks';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useState } from 'react';
type EventsKeywordSearchInputProps = {
  placeholder?: string;
  /** Max width of the input (defaults to a compact field opposite the heading). */
  maxWidth?: string | number | Record<string, string | number>;
};

/**
 * Debounced keyword search bound to the `search` query-string param.
 * Consumed by EventList / EventCalendar via useQueryState('search').
 */
export const EventsKeywordSearchInput = (props: EventsKeywordSearchInputProps) => {
  const [search, setSearch] = useQueryState('search', parseAsString);
  const [inputValue, setInputValue] = useState(search || '');
  const debouncedValue = useDebounce(inputValue, 300);

  // Push debounced keywords into the query string (null clears the param).
  useEffect(() => {
    const next = debouncedValue?.trim() ? debouncedValue.trim() : null;
    const current = search?.trim() ? search.trim() : null;
    if (next !== current) {
      void setSearch(next, {
        history: 'replace',
        shallow: true
      });
    }
  }, [debouncedValue, search, setSearch]);

  // Sync from URL when it changes externally (back/forward, shared links).
  // Skip when the URL still matches the in-flight debounce so keystrokes aren't wiped.
  useEffect(() => {
    const urlValue = search || '';
    if (urlValue !== inputValue && urlValue !== (debouncedValue || '')) {
      setInputValue(urlValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: react only to URL changes
  }, [search]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = inputValue?.trim() ? inputValue.trim() : null;
    void setSearch(next, {
      history: 'replace',
      shallow: true
    });
  };
  const handleClear = () => {
    setInputValue('');
    void setSearch(null, {
      history: 'replace',
      shallow: true
    });
  };
  return <Box flexShrink={0} maxWidth={props.maxWidth || {
    base: 'full',
    md: '280px'
  }} width={'full'}>
      <form onSubmit={handleSubmit}>
        <InputGroup startElement={<SearchIcon boxSize={3.5} color={'fg.muted'} />} endElement={inputValue ? <IconButton aria-label={'Clear search'} size={'xs'} variant={'ghost'} onClick={handleClear}>
                <CloseIcon boxSize={2} />
              </IconButton> : undefined}>
          <Input aria-label={props.placeholder || 'Search events'} paddingLeft={9} placeholder={props.placeholder || 'Search events...'} size={'md'} type={'search'} value={inputValue} onChange={e => setInputValue(e.target.value)} />
        </InputGroup>
      </form>
    </Box>;
};